// Manutenção preventiva (pedido do titular, 08/10/2026): a lista pronta de
// itens e as contas de quando cada um vence. Vence pelo km ou pelo tempo, o
// que chegar primeiro, contando da última vez feita.
import { diasEntre } from './datas.js';
import { somarMeses } from './despesas.js';
import { data as formatarData, numeroBR, plural } from './formato.js';

// A lista pronta: os intervalos são um ponto de partida (cada frota ajusta
// no plano do caminhão, pelo manual e pelo uso).
export const CATALOGO = [
  { codigo: 'oleo_motor', nome: 'Óleo do motor e filtros', km: 20000, meses: 6 },
  { codigo: 'oleo_cambio', nome: 'Óleo do câmbio', km: 120000, meses: 24 },
  { codigo: 'oleo_diferencial', nome: 'Óleo do diferencial', km: 120000, meses: 24 },
  { codigo: 'filtro_ar', nome: 'Filtro de ar', km: 40000, meses: 12 },
  { codigo: 'filtro_combustivel', nome: 'Filtro de combustível', km: 20000, meses: 6 },
  { codigo: 'graxa', nome: 'Graxa (lubrificação)', km: 10000, meses: 1 },
  { codigo: 'freios', nome: 'Freios', km: 40000, meses: 6 },
  { codigo: 'rodizio_pneus', nome: 'Rodízio dos pneus', km: 20000, meses: 6 },
  { codigo: 'revisao_geral', nome: 'Revisão geral', km: 60000, meses: 12 },
  { codigo: 'tacografo', nome: 'Aferição do tacógrafo', km: null, meses: 24 },
  { codigo: 'extintor', nome: 'Extintor', km: null, meses: 12 },
];

const ESTADOS = { vencida: 0, perto: 1, sem_base: 2, ok: 3 };

const numero = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

// O km mais alto que se conhece do caminhão (o hodômetro só sobe): das
// leituras (abastecimentos e despesas com km), do hodômetro inicial e das
// últimas vezes dos itens. Devolve { km, data } ou null.
export function kmAtual(veiculo, leituras = [], itens = []) {
  let maior = null;
  const ver = (km, dia) => {
    const n = numero(km);
    if (n === null || !(n >= 0)) return;
    // No mesmo km, vale o primeiro dia em que ele apareceu (a leitura).
    if (!maior || n > maior.km || (n === maior.km && dia && (!maior.data || dia < maior.data))) maior = { km: n, data: dia ?? null };
  };
  if (veiculo) ver(veiculo.hodometro_inicial, null);
  for (const l of leituras) if (!l.deleted_at && l.veiculo_id === veiculo?.id) ver(l.hodometro, l.data);
  for (const i of itens) {
    if (i.deleted_at || i.veiculo_id !== veiculo?.id) continue;
    ver(i.base_km, i.base_data);
    ver(i.feita_km, i.feita_data);
  }
  return maior;
}

// A última vez feita: a data mais recente e o km mais alto, entre a base
// (informada no plano), a feita (o banco anota pelas despesas) e as despesas
// deste aparelho que marcam o item (ainda sem resposta do servidor também).
export function ultimaVez(item, lancamentos = []) {
  const vezes = [
    { data: item.base_data ?? null, km: numero(item.base_km) },
    { data: item.feita_data ?? null, km: numero(item.feita_km) },
    ...lancamentos
      .filter((l) => !l.deleted_at && (l.manutencao_itens ?? []).includes(item.id))
      .map((l) => ({ data: l.data, km: numero(l.hodometro) })),
  ];
  const datas = vezes.map((v) => v.data).filter(Boolean).sort();
  const kms = vezes.map((v) => v.km).filter((k) => k !== null);
  return { data: datas.at(-1) ?? null, km: kms.length > 0 ? Math.max(...kms) : null };
}

// Quanto antes avisar: 10% do intervalo, com o mínimo de 1.000 km e 15 dias.
export function antecedencia(item) {
  return {
    km: item.intervalo_km ? Math.max(1000, Math.round(item.intervalo_km * 0.1)) : null,
    dias: item.intervalo_meses ? Math.max(15, Math.round(item.intervalo_meses * 3)) : null,
  };
}

// Quando vence e quanto falta. estado: 'vencida', 'perto', 'ok' ou
// 'sem_base' (não dá para contar: falta a última vez, ou o km do caminhão).
export function situacaoDoItem(item, { ultima, km = null, hoje }) {
  const intervaloKm = numero(item.intervalo_km);
  const meses = numero(item.intervalo_meses);
  const proximoKm = intervaloKm && ultima.km !== null ? ultima.km + intervaloKm : null;
  const proximaData = meses && ultima.data ? somarMeses(ultima.data, meses) : null;
  const faltaKm = proximoKm !== null && km !== null ? proximoKm - km : null;
  const faltaDias = proximaData ? diasEntre(hoje, proximaData) : null;
  const aviso = antecedencia(item);
  let estado = 'ok';
  if (faltaKm === null && faltaDias === null) estado = 'sem_base';
  else if ((faltaKm !== null && faltaKm <= 0) || (faltaDias !== null && faltaDias <= 0)) estado = 'vencida';
  else if ((faltaKm !== null && faltaKm <= aviso.km) || (faltaDias !== null && faltaDias <= aviso.dias)) estado = 'perto';
  return { estado, proximoKm, proximaData, faltaKm, faltaDias, ultima };
}

// "A cada 20.000 km ou 6 meses"
export function textoDoIntervalo(item) {
  const partes = [];
  if (item.intervalo_km) partes.push(`${numeroBR(item.intervalo_km)} km`);
  if (item.intervalo_meses) partes.push(plural(Number(item.intervalo_meses), 'mês', 'meses'));
  return `A cada ${partes.join(' ou ')}`;
}

// O que falta, numa frase: "Venceu há 300 km", "Faltam 1.200 km",
// "Vence em 12 dias (20/10/2026)". O que vence primeiro vem na frente.
export function textoDaSituacao(s) {
  if (s.estado === 'sem_base') return 'Informe quando foi feita pela última vez';
  const porKm = s.faltaKm === null ? null
    : s.faltaKm <= 0 ? (s.faltaKm === 0 ? 'Vence agora' : `Venceu há ${numeroBR(-s.faltaKm)} km`)
      : `Faltam ${numeroBR(s.faltaKm)} km`;
  const porData = s.faltaDias === null ? null
    : s.faltaDias < 0 ? `Venceu em ${formatarData(s.proximaData)}`
      : s.faltaDias === 0 ? 'Vence hoje'
        : `Vence em ${plural(s.faltaDias, 'dia')} (${formatarData(s.proximaData)})`;
  if (!porKm) return porData;
  if (!porData) return porKm;
  // O que está mais perto (em proporção do aviso) primeiro
  const kmPrimeiro = s.faltaKm <= 0 || (s.faltaDias > 0 && s.faltaKm / 1000 <= s.faltaDias / 15);
  return kmPrimeiro ? `${porKm} · ${porData}` : `${porData} · ${porKm}`;
}

// "Próxima: 520.000 km ou 01/04/2027"
export function textoDaProxima(s) {
  const partes = [];
  if (s.proximoKm !== null) partes.push(`${numeroBR(s.proximoKm)} km`);
  if (s.proximaData) partes.push(formatarData(s.proximaData));
  return partes.length > 0 ? `Próxima: ${partes.join(' ou ')}` : null;
}

// "Última vez: 01/04/2026 · 480.000 km"
export function textoDaUltima(u) {
  const partes = [];
  if (u.data) partes.push(formatarData(u.data));
  if (u.km !== null) partes.push(`${numeroBR(u.km)} km`);
  return partes.length > 0 ? `Última vez: ${partes.join(' · ')}` : 'Última vez: não informada';
}

// Todos os itens de um caminhão, com a situação, os que pedem atenção primeiro.
export function planoDoVeiculo(veiculo, { itens, leituras, lancamentos = leituras, hoje }) {
  const doVeiculo = itens.filter((i) => !i.deleted_at && i.veiculo_id === veiculo.id);
  const atual = kmAtual(veiculo, leituras, doVeiculo);
  return doVeiculo
    .map((item) => ({ item, situacao: situacaoDoItem(item, { ultima: ultimaVez(item, lancamentos), km: atual?.km ?? null, hoje }) }))
    .sort((a, b) => ESTADOS[a.situacao.estado] - ESTADOS[b.situacao.estado]
      || (a.situacao.faltaKm ?? Infinity) - (b.situacao.faltaKm ?? Infinity)
      || (a.situacao.faltaDias ?? Infinity) - (b.situacao.faltaDias ?? Infinity)
      || a.item.nome.localeCompare(b.item.nome, 'pt-BR'));
}

// Os avisos da frota (Início): itens vencidos e perto de vencer de cada
// caminhão ativo, os vencidos primeiro.
export function avisosDaFrota({ veiculos, itens, leituras, lancamentos = leituras, hoje }) {
  const avisos = [];
  for (const veiculo of veiculos) {
    if (!veiculo.ativo || veiculo.deleted_at) continue;
    for (const p of planoDoVeiculo(veiculo, { itens, leituras, lancamentos, hoje })) {
      if (p.situacao.estado === 'vencida' || p.situacao.estado === 'perto') avisos.push({ veiculo, ...p });
    }
  }
  return avisos.sort((a, b) => ESTADOS[a.situacao.estado] - ESTADOS[b.situacao.estado]);
}

// Os itens da lista pronta que ainda não estão no plano do caminhão.
export function faltamNoPlano(itensDoVeiculo) {
  const tem = new Set(itensDoVeiculo.filter((i) => !i.deleted_at && i.codigo).map((i) => i.codigo));
  return CATALOGO.filter((c) => !tem.has(c.codigo));
}
